/**
 * Order List Component for RMC Two-Panel Layout
 * Displays list of orders in a table format matching Figma design
 */

import {trackEvent} from '@hconnect/common/logging/Analytics'
import {Add} from '@mui/icons-material'
import {Box, Button, Typography} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {useMediaQueryBreakpoints} from '../../../../../common/useMediaQueryBreakpoints'
import {useOrderIntakeData} from '../../../../../OrderIntake/hooks/useOrderIntakeData'
import {ContactSection} from '../../../shared/order-editor/components/fields/Contact/ContactSection'
import {RmcOrder} from '../../types/order'

import {
  getAddItemButtonStyles,
  getContainerStyles,
  getSectionTitleStyles
} from './RmcOrderList.styles'
import {OrderCardView, OrderTableView, PlaceOrderButton} from './RmcOrderListComponents'

/**
 * Props for the RMC Order List component
 */
interface RmcOrderListProps {
  orders: RmcOrder[]
  selectedOrderId: string | null
  onSelectOrder: (orderId: string | null) => void
  onDeleteOrder: (orderId: string) => void
  onAddItem?: () => void
  onBackToStepper?: () => void // Keep for backward compatibility but make optional
  // Added props for place order button
  isPlacingOrders?: boolean
  canPlaceOrders?: boolean
  onPlaceOrders?: () => Promise<void>
}

/**
 * Order List component matching Figma design
 * Responsive: Table view on desktop, card view on mobile
 */
export const RmcOrderList: React.FC<RmcOrderListProps> = ({
  orders,
  selectedOrderId,
  onSelectOrder,
  onDeleteOrder,
  onAddItem,
  onBackToStepper,
  // Added props for place order button
  isPlacingOrders = false,
  canPlaceOrders = false,
  onPlaceOrders
}) => {
  const {t, i18n} = useTranslation()
  const {sm: isMobile} = useMediaQueryBreakpoints()
  // Get user profile data for contact defaults
  const {userProfileCountry} = useOrderIntakeData()

  // Handle add item click with analytics tracking
  const handleAddItemClick = () => {
    // Track add item button click
    trackEvent('rmcAddItemClicked', {
      businessLine: 'RMC',
      isFirstItem: orders.length === 0,
      currentOrderCount: orders.length,
      hasSelectedOrder: !!selectedOrderId,
      selectedOrderId: selectedOrderId
    })

    // Call the appropriate handler
    if (onAddItem) {
      onAddItem()
    }
  }

  return (
    <Box sx={getContainerStyles({isMobile})}>
      {/* Mix and Delivery Time Section */}
      <Box>
        <Typography sx={getSectionTitleStyles({isMobile})}>
          {t('orderIntake.rmc.orderManagement.mixAndDeliveryTime')}
        </Typography>

        {isMobile ? (
          /* Mobile Card View */
          <OrderCardView
            orders={orders}
            selectedOrderId={selectedOrderId}
            onSelectOrder={onSelectOrder}
            onDeleteOrder={onDeleteOrder}
            t={t}
            language={i18n.language}
          />
        ) : (
          /* Desktop Table View */
          <OrderTableView
            orders={orders}
            selectedOrderId={selectedOrderId}
            onSelectOrder={onSelectOrder}
            onDeleteOrder={onDeleteOrder}
            t={t}
            language={i18n.language}
          />
        )}

        {/* Add Item Button */}
        <Box sx={{paddingTop: '16px'}}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddItemClick}
            disabled={(!onAddItem && !onBackToStepper) || (onAddItem && orders.length === 0)}
            sx={getAddItemButtonStyles()}
            data-test-id="add-item-button"
          >
            {orders.length === 0
              ? t('orderIntake.rmc.orderManagement.addFirstItem', 'Add first item')
              : t('orderIntake.rmc.orderManagement.addItem', 'Add item')}
          </Button>
        </Box>
      </Box>

      {/* Contact Details Section */}
      <Box>
        <ContactSection userProfileCountry={userProfileCountry} />

        {/* Mobile-only Place Order Button */}
        {isMobile && onPlaceOrders && (
          <PlaceOrderButton
            isPlacingOrders={isPlacingOrders}
            canPlaceOrders={canPlaceOrders}
            hasOrders={orders.length > 0}
            onPlaceOrders={onPlaceOrders}
            t={t}
          />
        )}
      </Box>
    </Box>
  )
}
